import React from 'react';
import Markdown from 'markdown-to-jsx';
import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import LockIllustration from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/lock-closed/index.tsx';
import RopeIllustration from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/rope-stanchion/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { Anchor } from '../../../../common/ui/anchor/index.tsx';
import { TransitionHomeContentBox } from '../../../../common/ui/transition-home/content-box/index.tsx';
import taskMessages from '../../../../common/utils/tasks/messages.tsx';
import {
	useGetUsersWithoutAdminRole,
	useGetUsersWithoutCustomRole,
} from '../../../../services/store/users-and-roles/index.tsx';
import { messages } from './messages.tsx';

export const UsersAndRolesStepPrimaryAdmin = () => {
	const { formatMessage } = useIntl();
	const [usersWithoutAdminRole] = useGetUsersWithoutAdminRole();
	const [usersWithoutCustomRole] = useGetUsersWithoutCustomRole();

	if (usersWithoutAdminRole.data === null || usersWithoutCustomRole.data === null) {
		// Users should not see this screen if one of the requests is still loading or error
		throw new Error(
			'UsersAndRolesStepPrimaryAdmin should not be rendered if users are still loading',
		);
	}

	return (
		<>
			{/* Task One */}
			<TransitionHomeContentBox illustration={<RopeIllustration width={168} alt="" />}>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.usersAndRolesTaskOneNonFinal} />
					</Heading>
					<Box>
						<Markdown>{formatMessage(messages.taskOneDescriptionPrimaryAdminNonFinal)}</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Two */}
			<TransitionHomeContentBox illustration={<LockIllustration width={100} alt="" />}>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage
							{...(fg('ops_admin_role_release')
								? taskMessages.usersAndRolesTaskTwoV2NonFinal
								: taskMessages.usersAndRolesTaskTwoNonFinal)}
							values={{
								count: usersWithoutAdminRole.data.count,
							}}
						/>
					</Heading>
					<Box>
						<Markdown options={{ overrides: { a: { component: Anchor } } }}>
							{usersWithoutAdminRole.data.count > 0
								? formatMessage(
										fg('ops_admin_role_release')
											? messages.taskTwoDescriptionPrimaryAdminV2NonFinal
											: messages.taskTwoDescriptionPrimaryAdminNonFinal,
									)
								: formatMessage(
										fg('ops_admin_role_release')
											? messages.taskTwoEmptyDescriptionPrimaryAdminV2NonFinal
											: messages.taskTwoEmptyDescriptionPrimaryAdminNonFinal,
									)}
						</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Three */}
			<TransitionHomeContentBox illustration={<LockIllustration width={100} alt="" />}>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage
							{...taskMessages.usersAndRolesTaskThreeNonFinal}
							values={{
								count: usersWithoutCustomRole.data.count,
							}}
						/>
					</Heading>
					<Box>
						<Markdown options={{ overrides: { a: { component: Anchor } } }}>
							{usersWithoutCustomRole.data.count > 0
								? formatMessage(messages.taskThreeDescriptionPrimaryAdminNonFinal)
								: formatMessage(messages.taskThreeEmptyDescriptionPrimaryAdminNonFinal)}
						</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>
		</>
	);
};
